import React from "react";
import { Plus, ShieldCheck, Lock, Loader2, CreditCard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { base44 } from "@voult/api/base44Client";
import { useAuth } from "@voult/lib/AuthContext";
import VaultCard from "@voult/components/VaultCard";
import AddCardModal from "@voult/components/AddCardModal";

export default function Home() {
  const { user } = useAuth();
  const [cards, setCards] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [modalOpen, setModalOpen] = React.useState(false);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const data = await base44.entities.Card.list("-created_date", 100);
      setCards(data || []);
    } catch {
      setCards([]);
    } finally {
      setLoading(false);
    }
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (payload) => {
    await base44.entities.Card.create(payload);
    setModalOpen(false);
    await load();
  };

  const handleDelete = async (card) => {
    await base44.entities.Card.delete(card.id);
    setCards((prev) => prev.filter((c) => c.id !== card.id));
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-slate-50/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-900 text-white">
              <Lock className="h-4.5 w-4.5" />
            </div>
            <div>
              <h1 className="text-lg font-semibold tracking-tight text-slate-900">Voult</h1>
              <p className="-mt-0.5 text-[11px] text-slate-500">Secure card tokenization</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-slate-500 sm:block">
              {user?.email}
            </span>
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-3.5 py-2 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              <Plus className="h-4 w-4" />
              Add card
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-8">
        {/* Hero / stats */}
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 p-6 text-white sm:p-8"
        >
          <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="mb-3 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-1 text-[10px] font-medium uppercase tracking-widest text-white/70">
                <ShieldCheck className="h-3 w-3" /> PCI-Tokenized
              </div>
              <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">
                Your cards, safely tokenized.
              </h2>
              <p className="mt-2 max-w-md text-sm text-white/60">
                Every card you add is replaced with a secure token. The full number is never stored on Voult.
              </p>
            </div>
            <div className="flex gap-6">
              <div>
                <p className="text-3xl font-semibold">{cards.length}</p>
                <p className="text-xs text-white/50">Cards vaulted</p>
              </div>
              <div>
                <p className="text-3xl font-semibold">100%</p>
                <p className="text-xs text-white/50">Tokens secured</p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Cards grid */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20 text-slate-400">
            <Loader2 className="h-6 w-6 animate-spin" />
            <p className="mt-3 text-sm">Loading your vault…</p>
          </div>
        ) : cards.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-white py-20 text-center"
          >
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-100 text-slate-400">
              <CreditCard className="h-6 w-6" />
            </div>
            <h3 className="mt-4 text-base font-semibold text-slate-900">Your vault is empty</h3>
            <p className="mt-1 max-w-xs text-sm text-slate-500">
              Add your first card to instantly tokenize it and keep it secure.
            </p>
            <button
              onClick={() => setModalOpen(true)}
              className="mt-5 inline-flex items-center gap-1.5 rounded-xl bg-slate-900 px-4 py-2.5 text-sm font-medium text-white transition hover:bg-slate-800"
            >
              <Plus className="h-4 w-4" />
              Add your first card
            </button>
          </motion.div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            <AnimatePresence>
              {cards.map((card) => (
                <VaultCard key={card.id} card={card} onDelete={handleDelete} />
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      <AddCardModal open={modalOpen} onClose={() => setModalOpen(false)} onSave={handleSave} />
    </div>
  );
}